// Time-zone helpers without dependencies (Intl only).
import type { Interval } from './types.ts';

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

/** Offset of `timeZone` from UTC at instant `t`, in milliseconds (Berlin summer: +7_200_000). */
export function zoneOffsetMs(t: number, timeZone: string): number {
  const parts: Record<string, number> = {};
  for (const p of formatter(timeZone).formatToParts(new Date(t))) {
    if (p.type !== 'literal') parts[p.type] = Number(p.value);
  }
  const wall = Date.UTC(parts.year ?? 1970, (parts.month ?? 1) - 1, parts.day ?? 1, parts.hour ?? 0, parts.minute ?? 0, parts.second ?? 0);
  return wall - Math.floor(t / 1000) * 1000;
}

function localDate(t: number, timeZone: string): string {
  const parts: Record<string, string> = {};
  for (const p of formatter(timeZone).formatToParts(new Date(t))) parts[p.type] = p.value;
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/**
 * The UTC instant the local day `year-month-day` starts in `timeZone`. Month is 1-based; overflowing days roll over.
 * Usually local midnight; where daylight saving time begins at midnight (Santiago, Havana, the Azores) midnight does
 * not exist and the day starts at the jump (01:00). Where midnight occurs twice, the first one counts.
 */
export function zonedMidnight(year: number, month: number, day: number, timeZone: string): number {
  const wall = Date.UTC(year, month - 1, day);
  const target = new Date(wall).toISOString().slice(0, 10);
  // Midnight under the offset in force before (a UTC+14 day starts 14 hours before `wall`), at and after the day's
  // start; the earliest one on that day wins.
  let start = Number.POSITIVE_INFINITY;
  for (const probe of [wall - 50_400_000, wall, wall + 43_200_000]) {
    const t = wall - zoneOffsetMs(probe, timeZone);
    if (t < start && localDate(t, timeZone) === target) start = t;
  }
  if (Number.isFinite(start)) return start;
  // No instant has that date (Samoa skipped 30 December 2011): the day is empty and starts where the next one does.
  let t = wall - zoneOffsetMs(wall, timeZone);
  t = wall - zoneOffsetMs(t, timeZone);
  const shown = t + zoneOffsetMs(t, timeZone);
  if (shown < wall) t += wall - shown;
  return t;
}

/** [start, end) of a local calendar day (`YYYY-MM-DD`). A daylight-saving day may be 23 or 25 hours long. */
export function localDayRange(day: string, timeZone: string): Interval {
  const [y, m, d] = day.split('-').map(Number);
  if (y === undefined || m === undefined || d === undefined) throw new Error(`Invalid day: ${day}`);
  return { start: zonedMidnight(y, m, d, timeZone), end: zonedMidnight(y, m, d + 1, timeZone) };
}
